import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  collectVersions,
  type OnFarmImplantFreezingEmbryo,
  OnFarmImplantRequestError,
  useCompleteOnFarmImplantFreezing,
} from '@/services/on-farm-implant';
import { useToast } from '@/services/toast';

type CompleteFreezingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  rows: OnFarmImplantFreezingEmbryo[];
  onCompleted: () => void;
};

export const CompleteFreezingDialog = ({
  open,
  onOpenChange,
  sessionId,
  rows,
  onCompleted,
}: CompleteFreezingDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: complete, isPending } =
    useCompleteOnFarmImplantFreezing();

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) setProblems([]);
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleConfirm = useCallback(async () => {
    if (rows.length === 0) return;

    setProblems([]);

    try {
      const result = await complete({
        sessionId,
        input: {
          sessionEmbryoIds: rows.map((row) => row.sessionEmbryoId),
          versions: collectVersions(rows),
        },
      });

      onCompleted();
      handleOpenChange(false);
      toast.show({
        severity: 'success',
        summary: t('onFarmImplant.onFarmFreezing.complete.success', {
          count: result.completed,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }

      console.error('Failed to complete On-Farm Freezing:', error);
      toast.show({
        severity: 'error',
        summary: t('onFarmImplant.onFarmFreezing.complete.failed'),
      });
    }
  }, [complete, handleOpenChange, onCompleted, rows, sessionId, t, toast]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.onFarmFreezing.complete.title')}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.onFarmFreezing.complete.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-sm font-medium text-gray-700">
            {t('onFarmImplant.onFarmFreezing.apply.recordCount', {
              count: rows.length,
            })}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={rows.length === 0 || isPending}
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.onFarmFreezing.complete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
